import {
  CAPTAIN_OPTION_1_INCLUDED_IN_PRICE,
  CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN,
  CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE,
} from 'src/common/constants';
import type { CaptainOptions } from 'src/common/types/CaptainOptions';

/** Used for translation keys (these string literals are unknown to backend APIs) */
type CaptainOptionTranslationKeys = 'captain-included-in-price' | 'captain-not-included-in-price' | 'independent-captain' | 'captain-optional';

/**
 * Consider a listing's new captain options configuration;
 *
 * We get `captain_options` from the API, which is an array of numbers that replaces the
 * deprecated `captain_option`, `bareboat`, and `captained` fields.
 *
 * For `captain_options`, there are only 4 valid options returned by the API:
 * - [1]: Captain included in price
 * - [2]: Independent captain
 * - [4]: Captain not included in price
 * - [2, 4]: Captain optional
 *
 * There are only 4 options, not 7, because the "Captain included in price" option cannot
 * be combined with any other captain option.
 *
 *```
 *     __________
  *   │    [1]   │ -> 1 -> Captain included in price
  *   │    [2]   │ -> 2 -> Independent Captain
  *   │    [4]   │ -> 4 -> Captain not included in price
  *   │  [2, 4]  │ -> 6 -> Captain Optional
  *   │  [1, 2]  │ -> INVALID
  *   │  [1, 4]  │ -> INVALID
  *   │[1, 2, 4] │ -> INVALID
  *   |__________|
  * ```
  */

const getCaptainOption = (
  captainOptions: CaptainOptions,
): CaptainOptionTranslationKeys | undefined => {
  if (!captainOptions) return undefined;

  if (captainOptions.length === 1 && captainOptions[0] === CAPTAIN_OPTION_1_INCLUDED_IN_PRICE) {
    return 'captain-included-in-price';
  }
  if (captainOptions.length === 1 && captainOptions[0] === CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN) {
    return 'independent-captain';
  }
  if (captainOptions.length === 1 && captainOptions[0] === CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE) {
    return 'captain-not-included-in-price';
  }
  if (
    captainOptions.length === 2
    && captainOptions.includes(CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN)
    && captainOptions.includes(CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE)
  ) {
    return 'captain-optional';
  }
  return undefined;
};

export default getCaptainOption;
